import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calendar, Clock, Plus, Users, ChevronLeft, ChevronRight,
  X, Check, Loader2, Edit, Trash2, MapPin, Ban, AlertCircle,
  CheckCircle, XCircle, FileText, Info
} from 'lucide-react';
import { TemplateModal } from './TemplateModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHIFT_TYPES = [
  { value: 'mattina', label: 'Mattina', time: '9:30-12:30', color: 'bg-amber-100 text-amber-800' },
  { value: 'pomeriggio', label: 'Pomeriggio', time: '15:30-18:30', color: 'bg-blue-100 text-blue-800' }
];

const ROOMS = [
  { value: 'Stanza A', label: 'Stanza A', color: 'bg-sky-100' },
  { value: 'Stanza B', label: 'Stanza B', color: 'bg-purple-100' }
];

const SHIFT_STATUS = {
  pending: { label: 'In attesa', color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
  confirmed: { label: 'Confermato', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  declined: { label: 'Rifiutato', color: 'bg-red-100 text-red-800', icon: XCircle }
};

export const ShiftsManager = ({ isAdmin = true, currentUserId = null }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [calendarData, setCalendarData] = useState({ calendar: {}, shifts: [] });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUnavailModal, setShowUnavailModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showQuarterlyModal, setShowQuarterlyModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictShiftData, setConflictShiftData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pendingShifts, setPendingShifts] = useState([]);
  const [unavailabilities, setUnavailabilities] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [quarterlyDates, setQuarterlyDates] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [generatingShifts, setGeneratingShifts] = useState(false);
  const [quarterlyShiftType, setQuarterlyShiftType] = useState('');
  const [quarterlyReason, setQuarterlyReason] = useState('');
  
  // Location filter
  const [locationFilter, setLocationFilter] = useState('tutti'); // 'tutti' | 'milano' | 'napoli'
  
  // Appointments count per day
  const [appointmentCounts, setAppointmentCounts] = useState({});
  
  // Vincoli turni: max 2 oculisti + 2 ortottisti per mattina/pomeriggio
  const MAX_OCULISTI_PER_SHIFT = 2;
  const MAX_ORTOTTISTI_PER_SHIFT = 2;
  
  const [formData, setFormData] = useState({
    staff_id: '',
    date: '',
    shift_type: 'mattina',
    room: 'Stanza A',
    location: 'milano',
    paired_with_id: '',
    notes: ''
  });
  
  const [unavailFormData, setUnavailFormData] = useState({
    date: '',
    shift_type: '',
    reason: ''
  });

  useEffect(() => {
    loadCalendar();
    if (isAdmin) loadStaff();
    loadPendingShifts();
    loadUnavailabilities();
  }, [currentMonth, locationFilter]);

  // Gestione apertura modal conflitto da URL
  useEffect(() => {
    const conflictShiftId = searchParams.get('conflict_shift_id');
    if (conflictShiftId) {
      loadConflictDetails(conflictShiftId);
    }
  }, [searchParams]);

  const loadConflictDetails = async (shiftId) => {
    try {
      const response = await axios.get(`${API}/hr/shifts/${shiftId}`, getAuthHeaders());
      if (response.data) {
        setConflictShiftData(response.data);
        setShowConflictModal(true);
        // Rimuovi il parametro dall'URL dopo aver aperto il modal
        searchParams.delete('conflict_shift_id');
        setSearchParams(searchParams);
      }
    } catch (error) {
      console.error('Error loading conflict shift:', error);
      toast.error('Errore nel caricamento dei dettagli del conflitto');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const loadCalendar = async () => {
    setLoading(true);
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      let url = `${API}/hr/shifts/calendar?month=${month}&year=${year}`;
      if (locationFilter && locationFilter !== 'tutti') {
        url += `&location=${locationFilter}`;
      }
      const response = await axios.get(url, getAuthHeaders());
      setCalendarData(response.data);
      
      // Load appointment counts for this month
      loadAppointmentCounts(month, year);
    } catch (error) {
      console.error('Error loading calendar:', error);
      toast.error('Errore nel caricamento del calendario');
    } finally {
      setLoading(false);
    }
  };
  
  const loadAppointmentCounts = async (month, year) => {
    try {
      const response = await axios.get(
        `${API}/appointments/calendar?month=${month}&year=${year}${locationFilter !== 'tutti' ? `&location=${locationFilter}` : ''}`,
      );
      const counts = {};
      (response.data.days || []).forEach(day => {
        if (day.booked_count > 0) {
          counts[day.date] = day.booked_count;
        }
      });
      setAppointmentCounts(counts);
    } catch (error) {
      console.error('Error loading appointment counts:', error);
    }
  };

  const loadStaff = async () => {
    try {
      const response = await axios.get(`${API}/hr/reports/staff-list`, getAuthHeaders());
      setStaff(response.data.staff || []);
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  const loadPendingShifts = async () => {
    try {
      const response = await axios.get(`${API}/hr/shifts/pending`, getAuthHeaders());
      setPendingShifts(response.data.shifts || []);
    } catch (error) {
      console.error('Error loading pending shifts:', error);
    }
  };

  const loadUnavailabilities = async () => {
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      const dateFrom = `${year}-${String(month).padStart(2, '0')}-01`;
      const dateTo = month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`;
      
      const response = await axios.get(
        `${API}/hr/unavailability?date_from=${dateFrom}&date_to=${dateTo}`,
        getAuthHeaders()
      );
      setUnavailabilities(response.data.unavailabilities || []);
    } catch (error) {
      console.error('Error loading unavailabilities:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/hr/shift-templates`, getAuthHeaders());
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  // Conferma/Rifiuta turno (per staff)
  const handleConfirmShift = async (shiftId) => {
    try {
      await axios.put(`${API}/hr/shifts/${shiftId}/confirm`, {}, getAuthHeaders());
      toast.success('Turno confermato!');
      loadCalendar();
      loadPendingShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella conferma');
    }
  };

  const handleDeclineShift = async (shiftId, reason = '') => {
    try {
      await axios.put(`${API}/hr/shifts/${shiftId}/decline?reason=${encodeURIComponent(reason)}`, {}, getAuthHeaders());
      toast.success('Turno rifiutato');
      loadCalendar();
      loadPendingShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel rifiuto');
    }
  };

  // Gestione indisponibilità
  const handleAddUnavailability = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        date: unavailFormData.date,
        shift_type: unavailFormData.shift_type || null,
        reason: unavailFormData.reason || null
      };
      const response = await axios.post(`${API}/hr/unavailability`, payload, getAuthHeaders());
      toast.success(`Indisponibilità registrata${response.data.conflicts > 0 ? ` (${response.data.conflicts} conflitti segnalati)` : ''}`);
      setShowUnavailModal(false);
      setUnavailFormData({ date: '', shift_type: '', reason: '' });
      loadUnavailabilities();
      loadCalendar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella registrazione');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUnavailability = async (unavailId) => {
    if (!window.confirm('Rimuovere questa indisponibilità?')) return;
    try {
      await axios.delete(`${API}/hr/unavailability/${unavailId}`, getAuthHeaders());
      toast.success('Indisponibilità rimossa');
      loadUnavailabilities();
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  // ========== GESTIONE TRIMESTRALE ==========
  
  // Toggle data per indisponibilità trimestrale
  const toggleQuarterlyDate = (dateStr) => {
    setQuarterlyDates(prev => {
      if (prev.includes(dateStr)) {
        return prev.filter(d => d !== dateStr);
      } else {
        return [...prev, dateStr];
      }
    });
  };

  // Salva indisponibilità trimestrale (batch)
  const handleSaveQuarterlyUnavailability = async (shiftType = null, reason = '') => {
    if (quarterlyDates.length === 0) {
      toast.error('Seleziona almeno una data');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/hr/unavailability/quarterly`, {
        dates: quarterlyDates,
        shift_type: shiftType || null,
        reason: reason || null
      }, getAuthHeaders());
      
      toast.success(`${response.data.created} indisponibilità registrate${response.data.conflicts.length > 0 ? ` (${response.data.conflicts.length} conflitti)` : ''}`);
      setQuarterlyDates([]);
      setQuarterlyShiftType('');
      setQuarterlyReason('');
      loadUnavailabilities();
      loadCalendar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Chiudi modal trimestrale con salvataggio automatico se ci sono date selezionate
  const handleCloseQuarterlyModal = async () => {
    if (quarterlyDates.length > 0) {
      // Chiedi conferma per salvare prima di chiudere
      const shouldSave = window.confirm(
        `Hai selezionato ${quarterlyDates.length} date di indisponibilità.\n\nVuoi salvare le modifiche prima di chiudere?`
      );
      
      if (shouldSave) {
        await handleSaveQuarterlyUnavailability(quarterlyShiftType || null, quarterlyReason || '');
      } else {
        // L'utente ha scelto di non salvare
        toast.info('Modifiche non salvate');
      }
    }
    
    // Reset e chiudi
    setShowQuarterlyModal(false);
    setQuarterlyDates([]);
    setQuarterlyShiftType('');
    setQuarterlyReason('');
  };

  // Verifica vincoli turni (max 2 oculisti + 2 ortottisti per turno)
  const checkShiftConstraints = (date, shiftType) => {
    const dayShifts = calendarData.shifts.filter(
      s => s.date === date && s.shift_type === shiftType && s.status !== 'declined'
    );
    
    const oculistiCount = dayShifts.filter(s => {
      const staffMember = staff.find(m => m.id === s.staff_id);
      return staffMember?.role === 'doctor' || staffMember?.staff_type === 'doctor';
    }).length;
    
    const ortottistiCount = dayShifts.filter(s => {
      const staffMember = staff.find(m => m.id === s.staff_id);
      return staffMember?.role === 'ortottista' || staffMember?.staff_type === 'ortottista';
    }).length;
    
    return {
      oculisti: { current: oculistiCount, max: MAX_OCULISTI_PER_SHIFT, canAdd: oculistiCount < MAX_OCULISTI_PER_SHIFT },
      ortottisti: { current: ortottistiCount, max: MAX_ORTOTTISTI_PER_SHIFT, canAdd: ortottistiCount < MAX_ORTOTTISTI_PER_SHIFT }
    };
  };

  // Genera turni trimestrali (admin only)
  const handleGenerateQuarterlyShifts = async () => {
    if (!window.confirm(`Generare turni automatici per Q${selectedQuarter}/${selectedYear}? Verranno creati turni per tutti i giorni lavorativi basati sulle disponibilità.`)) {
      return;
    }
    
    setGeneratingShifts(true);
    try {
      const response = await axios.post(
        `${API}/hr/shifts/generate-quarterly?quarter=${selectedQuarter}&year=${selectedYear}`,
        {},
        getAuthHeaders()
      );
      
      toast.success(`${response.data.total_shifts_created} turni generati per Q${selectedQuarter}/${selectedYear}. ${response.data.staff_notified} staff notificati.`);
      
      if (response.data.warnings?.length > 0) {
        toast.warning(`${response.data.warnings.length} avvisi: ${response.data.warnings[0]}`);
      }
      
      loadCalendar();
      loadPendingShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella generazione');
    } finally {
      setGeneratingShifts(false);
    }
  };

  // Elimina turni auto-generati del trimestre (admin only)
  const handleDeleteQuarterlyShifts = async () => {
    if (!window.confirm(`Eliminare tutti i turni auto-generati per Q${selectedQuarter}/${selectedYear}?`)) {
      return;
    }
    
    try {
      const response = await axios.delete(
        `${API}/hr/shifts/quarterly?quarter=${selectedQuarter}&year=${selectedYear}`,
        getAuthHeaders()
      );
      
      toast.success(response.data.message);
      loadCalendar();
      loadPendingShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  // Genera date del trimestre per la selezione
  const getQuarterDates = () => {
    const startMonth = (selectedQuarter - 1) * 3;
    const dates = [];
    
    for (let month = startMonth; month < startMonth + 3; month++) {
      const daysInMonth = new Date(selectedYear, month + 1, 0).getDate();
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(selectedYear, month, day);
        const dayOfWeek = date.getDay();
        // Solo giorni lavorativi (lun-ven)
        if (dayOfWeek >= 1 && dayOfWeek <= 5) {
          dates.push({
            dateStr: date.toISOString().split('T')[0],
            dayName: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'][dayOfWeek],
            day: day,
            month: month + 1,
            monthName: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'][month]
          });
        }
      }
    }
    return dates;
  };

  // Applica template
  const handleApplyTemplate = async (templateId, startDate) => {
    try {
      const response = await axios.post(
        `${API}/hr/shift-templates/${templateId}/apply?start_date=${startDate}`,
        {},
        getAuthHeaders()
      );
      toast.success(`${response.data.created} turni creati`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} problemi: ${response.data.errors.slice(0, 2).join(', ')}`);
      }
      loadCalendar();
      loadPendingShifts();
      setShowTemplateModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'applicazione template');
    }
  };

  const handleAddShift = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/hr/shifts`, formData, getAuthHeaders());
      toast.success('Turno creato e notifica inviata allo staff');
      setShowAddModal(false);
      resetForm();
      loadCalendar();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Errore nella creazione del turno';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo turno? Lo staff riceverà una notifica.')) return;
    try {
      await axios.delete(`${API}/hr/shifts/${shiftId}`, getAuthHeaders());
      toast.success('Turno eliminato e notifica inviata');
      loadCalendar();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const openAddModal = (date) => {
    setSelectedDate(date);
    setFormData({
      ...formData,
      date: date,
      staff_id: '',
      paired_with_id: '',
      location: locationFilter !== 'tutti' ? locationFilter : 'milano',
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      staff_id: '',
      date: '',
      shift_type: 'mattina',
      room: 'Stanza A',
      location: locationFilter !== 'tutti' ? locationFilter : 'milano',
      paired_with_id: '',
      notes: ''
    });
    setSelectedDate(null);
  };

  const getMonthDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    
    const days = [];
    
    // Padding giorni precedenti
    for (let i = startPadding - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, isCurrentMonth: false });
    }
    
    // Giorni del mese corrente
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const d = new Date(year, month, i);
      days.push({ date: d, isCurrentMonth: true });
    }
    
    // Padding giorni successivi
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(year, month + 1, i);
      days.push({ date: d, isCurrentMonth: false });
    }
    
    return days;
  };

  const formatDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const getShiftsForDate = (dateKey) => {
    return calendarData.calendar[dateKey] || { mattina: [], pomeriggio: [] };
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  const dayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

  const getRoleColor = (role) => {
    if (role === 'doctor') return 'bg-blue-500';
    if (role === 'ortottista') return 'bg-green-500';
    return 'bg-gray-500';
  };

  const getRoleLabel = (role) => {
    if (role === 'doctor') return 'Oculista';
    if (role === 'ortottista') return 'Ortottista';
    return role;
  };

  return (
    <div className="space-y-6" data-testid="shifts-manager">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Gestione Turni</h2>
          <p className="text-gray-500">Pianificazione turni staff medico</p>
        </div>
        
        {/* Legenda */}
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-blue-500"></span>
            <span>Oculista</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            <span>Ortottista</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs">Mattina 9:30-12:30</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">Pomeriggio 15:30-18:30</span>
          </div>
        </div>
      </div>
      
      {/* Location Filter Toggle */}
      <div className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
        locationFilter === 'milano' ? 'bg-orange-50 border-orange-400' : 
        locationFilter === 'napoli' ? 'bg-blue-50 border-blue-400' : 
        'bg-gray-50 border-gray-200'
      }`} data-testid="location-filter">
        <MapPin className="w-5 h-5" style={{ color: locationFilter === 'milano' ? '#F97316' : locationFilter === 'napoli' ? '#2A9FCB' : '#9CA3AF' }} />
        <span className="text-sm font-semibold" style={{ color: locationFilter === 'milano' ? '#9A3412' : locationFilter === 'napoli' ? '#1E40AF' : '#4B5563' }}>
          {locationFilter === 'tutti' ? 'Tutte le sedi' : `Sede: ${locationFilter === 'milano' ? 'Milano — Via Santa Sofia 10' : 'Napoli — Via Posillipo 160'}`}
        </span>
        <div className="ml-auto flex gap-2">
        <button
          onClick={() => setLocationFilter('tutti')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${locationFilter === 'tutti' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:bg-white/50'}`}
          data-testid="filter-all"
        >
          Tutte
        </button>
        <button
          onClick={() => setLocationFilter('milano')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${locationFilter === 'milano' ? 'bg-orange-500 text-white shadow-sm' : 'text-orange-600 hover:bg-orange-50'}`}
          data-testid="filter-milano"
        >
          Milano
        </button>
        <button
          onClick={() => setLocationFilter('napoli')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${locationFilter === 'napoli' ? 'bg-[#2A9FCB] text-white shadow-sm' : 'text-[#2A9FCB] hover:bg-blue-50'}`}
          data-testid="filter-napoli"
        >
          Napoli
        </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        {/* Turni in Attesa di Conferma */}
        {pendingShifts.length > 0 && (
          <button
            onClick={() => setShowPendingModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors"
            data-testid="pending-shifts-button"
          >
            <AlertCircle className="w-4 h-4" />
            {pendingShifts.length} Turni da Confermare
          </button>
        )}
        
        {/* Indisponibilità (per staff P.IVA) */}
        {!isAdmin && (
          <>
            <button
              onClick={() => setShowUnavailModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
              data-testid="add-unavailability-button"
            >
              <Ban className="w-4 h-4" />
              Segna Indisponibilità
            </button>
            <button
              onClick={() => setShowQuarterlyModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
              data-testid="quarterly-unavailability-button"
            >
              <Calendar className="w-4 h-4" />
              Indisponibilità Trimestrale
            </button>
          </>
        )}
        
        {/* Gestione Trimestrale (solo admin) */}
        {isAdmin && (
          <>
            <button
              onClick={() => setShowQuarterlyModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
              data-testid="quarterly-management-button"
            >
              <Calendar className="w-4 h-4" />
              Gestione Trimestrale
            </button>
            <button
              onClick={() => { loadTemplates(); setShowTemplateModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
              data-testid="templates-button"
            >
              <FileText className="w-4 h-4" />
              Template Settimanali
            </button>
          </>
        )}
      </div>

      {/* Calendario */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Header Calendario */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <button
            onClick={prevMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="prev-month-button"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <h3 className="text-lg font-semibold">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            {locationFilter !== 'tutti' && (
              <span className={`ml-3 text-sm font-semibold px-3 py-1 rounded-full ${locationFilter === 'milano' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                {locationFilter === 'milano' ? 'Milano' : 'Napoli'}
              </span>
            )}
          </h3>
          
          <button
            onClick={nextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="next-month-button"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
          </div>
        ) : (
          <div className="p-4">
            {/* Giorni della settimana */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Griglia giorni */}
            <div className="grid grid-cols-7 gap-1">
              {getMonthDays().map((dayData, idx) => {
                const dateKey = formatDateKey(dayData.date);
                const dayShifts = getShiftsForDate(dateKey);
                const hasShifts = dayShifts.mattina?.length > 0 || dayShifts.pomeriggio?.length > 0;
                const apptCount = appointmentCounts[dateKey] || 0;
                
                // Determine dominant location for border color
                const allShifts = [...(dayShifts.mattina || []), ...(dayShifts.pomeriggio || [])];
                const milanShifts = allShifts.filter(s => s.location === 'milano').length;
                const napoliShifts = allShifts.filter(s => s.location === 'napoli').length;
                let borderColor = '';
                if (milanShifts > 0 && napoliShifts === 0) borderColor = 'border-orange-300';
                else if (napoliShifts > 0 && milanShifts === 0) borderColor = 'border-blue-300';
                else if (milanShifts > 0 && napoliShifts > 0) borderColor = 'border-l-orange-400 border-r-blue-400 border-t-orange-400 border-b-blue-400';
                
                return (
                  <div
                    key={idx}
                    className={`min-h-[100px] p-1 border rounded-lg ${borderColor} ${
                      dayData.isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                    } ${isToday(dayData.date) ? 'ring-2 ring-sky-500' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1">
                        <span className={`text-sm font-medium ${
                          dayData.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                        } ${isToday(dayData.date) ? 'text-sky-600' : ''}`}>
                          {dayData.date.getDate()}
                        </span>
                        {apptCount > 0 && (
                          <span className="bg-sky-100 text-sky-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium" title={`${apptCount} prenotati`}>
                            {apptCount}
                          </span>
                        )}
                      </div>
                      
                      {isAdmin && dayData.isCurrentMonth && (
                        <button
                          onClick={() => openAddModal(dateKey)}
                          className="p-0.5 hover:bg-gray-100 rounded text-gray-400 hover:text-sky-600"
                          title="Aggiungi turno"
                          data-testid={`add-shift-${dateKey}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      )}
                    </div>

                    {/* Turni del giorno */}
                    <div className="space-y-0.5 text-xs">
                      {/* Mattina */}
                      {dayShifts.mattina?.map((shift, sIdx) => {
                        const status = SHIFT_STATUS[shift.status] || SHIFT_STATUS.pending;
                        const locColor = shift.location === 'milano' ? 'border-l-2 border-l-orange-400' : shift.location === 'napoli' ? 'border-l-2 border-l-blue-400' : '';
                        return (
                          <div 
                            key={sIdx} 
                            className={`flex items-center gap-1 px-1 py-0.5 rounded ${SHIFT_TYPES[0].color} ${locColor} ${shift.status === 'pending' ? 'opacity-70 border-r border-dashed border-r-yellow-500' : ''} ${shift.status === 'declined' ? 'opacity-50 line-through' : ''}`}
                            title={`${shift.staff_name} - ${shift.room} - ${status.label}${shift.location ? ' - ' + (shift.location === 'milano' ? 'Milano' : 'Napoli') : ''}`}
                          >
                            <span className={`w-2 h-2 rounded-full ${getRoleColor(shift.staff_role)}`}></span>
                            <span className="truncate flex-1">{shift.staff_name?.split(' ')[0]}</span>
                            {shift.status === 'pending' && <AlertCircle className="w-3 h-3 text-yellow-600" />}
                            {shift.status === 'confirmed' && <CheckCircle className="w-3 h-3 text-green-600" />}
                            <span className="text-[10px] opacity-70">{shift.room?.replace('Stanza ', '')}</span>
                            {isAdmin && (
                              <button
                                onClick={() => handleDeleteShift(shift.id)}
                                className="opacity-50 hover:opacity-100"
                              >
                                <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      );
                      })}
                      
                      {/* Pomeriggio */}
                      {dayShifts.pomeriggio?.map((shift, sIdx) => {
                        const status = SHIFT_STATUS[shift.status] || SHIFT_STATUS.pending;
                        const locColor = shift.location === 'milano' ? 'border-l-2 border-l-orange-400' : shift.location === 'napoli' ? 'border-l-2 border-l-blue-400' : '';
                        return (
                          <div 
                            key={sIdx} 
                            className={`flex items-center gap-1 px-1 py-0.5 rounded ${SHIFT_TYPES[1].color} ${locColor} ${shift.status === 'pending' ? 'opacity-70 border-r border-dashed border-r-yellow-500' : ''} ${shift.status === 'declined' ? 'opacity-50 line-through' : ''}`}
                            title={`${shift.staff_name} - ${shift.room} - ${status.label}${shift.location ? ' - ' + (shift.location === 'milano' ? 'Milano' : 'Napoli') : ''}`}
                          >
                            <span className={`w-2 h-2 rounded-full ${getRoleColor(shift.staff_role)}`}></span>
                            <span className="truncate flex-1">{shift.staff_name?.split(' ')[0]}</span>
                            {shift.status === 'pending' && <AlertCircle className="w-3 h-3 text-yellow-600" />}
                            {shift.status === 'confirmed' && <CheckCircle className="w-3 h-3 text-green-600" />}
                            <span className="text-[10px] opacity-70">{shift.room?.replace('Stanza ', '')}</span>
                            {isAdmin && (
                              <button
                                onClick={() => handleDeleteShift(shift.id)}
                                className="opacity-50 hover:opacity-100"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-sky-50 rounded-xl p-4">
        <h4 className="font-medium text-sky-800 mb-2">📋 Informazioni Turni</h4>
        <ul className="text-sm text-sky-700 space-y-1">
          <li>• <strong>Turno Mattina:</strong> 9:30 - 12:30 (9 visite da 20 min)</li>
          <li>• <strong>Turno Pomeriggio:</strong> 15:30 - 18:30 (9 visite da 20 min)</li>
          <li>• <strong>Stanze:</strong> A e B</li>
          <li>• <strong>Max per stanza/turno:</strong> 2 Oculisti + 2 Ortottisti</li>
          <li>• Il binomio Oculista + Ortottista è consigliato (90% dei casi), ma possono lavorare anche indipendentemente</li>
          <li>• <strong>Conferma:</strong> Lo staff (P.IVA) deve confermare o rifiutare i turni proposti</li>
          <li>• <strong>Indisponibilità:</strong> Lo staff può segnare le proprie indisponibilità</li>
        </ul>
      </div>

      {/* Modal Aggiungi Turno */}
      {showAddModal && isAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                Nuovo Turno - {selectedDate}
              </h3>
              <button 
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleAddShift} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Staff *</label>
                <select
                  value={formData.staff_id}
                  onChange={(e) => setFormData({...formData, staff_id: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                  required
                  data-testid="staff-select"
                >
                  <option value="">Seleziona staff</option>
                  {staff.filter(s => s.role === 'doctor' || s.role === 'ortottista').map(s => (
                    <option key={s.id} value={s.id}>
                      {getRoleLabel(s.role)} - {s.first_name} {s.last_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Turno *</label>
                <div className="grid grid-cols-2 gap-2">
                  {SHIFT_TYPES.map(shift => (
                    <button
                      key={shift.value}
                      type="button"
                      onClick={() => setFormData({...formData, shift_type: shift.value})}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.shift_type === shift.value
                          ? 'border-sky-500 bg-sky-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`shift-type-${shift.value}`}
                    >
                      <div className="font-medium">{shift.label}</div>
                      <div className="text-sm text-gray-500">{shift.time}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stanza *</label>
                <div className="grid grid-cols-2 gap-2">
                  {ROOMS.map(room => (
                    <button
                      key={room.value}
                      type="button"
                      onClick={() => setFormData({...formData, room: room.value})}
                      className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                        formData.room === room.value
                          ? 'border-sky-500 bg-sky-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`room-${room.value}`}
                    >
                      <MapPin className="w-4 h-4" />
                      {room.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sede *</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, location: 'milano'})}
                    className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                      formData.location === 'milano'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="location-milano-btn"
                  >
                    <MapPin className="w-4 h-4" />
                    Milano
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, location: 'napoli'})}
                    className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                      formData.location === 'napoli'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="location-napoli-btn"
                  >
                    <MapPin className="w-4 h-4" />
                    Napoli
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Binomio (opzionale)</label>
                <select
                  value={formData.paired_with_id}
                  onChange={(e) => setFormData({...formData, paired_with_id: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                  data-testid="paired-select"
                >
                  <option value="">Nessun binomio</option>
                  {staff
                    .filter(s => s.id !== formData.staff_id && (s.role === 'doctor' || s.role === 'ortottista'))
                    .map(s => (
                      <option key={s.id} value={s.id}>
                        {getRoleLabel(s.role)} - {s.first_name} {s.last_name}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Associa un collega per formare il binomio Oculista-Ortottista
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                  placeholder="Note aggiuntive..."
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving || !formData.staff_id}
                  className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50"
                  data-testid="save-shift-button"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Proponi Turno
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Turni in Attesa di Conferma */}
      {showPendingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-500" />
                Turni da Confermare ({pendingShifts.length})
              </h3>
              <button onClick={() => setShowPendingModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {pendingShifts.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nessun turno in attesa</p>
              ) : (
                pendingShifts.map(shift => (
                  <div key={shift.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-800">{shift.date}</p>
                        <p className="text-sm text-gray-600">
                          {shift.shift_type === 'mattina' ? 'Mattina (9:30-12:30)' : 'Pomeriggio (15:30-18:30)'}
                        </p>
                        <p className="text-sm text-gray-500">{shift.room}</p>
                        {shift.paired_with_name && (
                          <p className="text-xs text-sky-600 mt-1">Con: {shift.paired_with_name}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleConfirmShift(shift.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
                          data-testid={`confirm-shift-${shift.id}`}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Conferma
                        </button>
                        <button
                          onClick={() => {
                            const reason = window.prompt('Motivo del rifiuto (opzionale):');
                            if (reason !== null) handleDeclineShift(shift.id, reason);
                          }}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
                          data-testid={`decline-shift-${shift.id}`}
                        >
                          <XCircle className="w-4 h-4" />
                          Rifiuta
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Indisponibilità */}
      {showUnavailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-500" />
                Segna Indisponibilità
              </h3>
              <button onClick={() => setShowUnavailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleAddUnavailability} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                <input
                  type="date"
                  value={unavailFormData.date}
                  onChange={(e) => setUnavailFormData({...unavailFormData, date: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                  required
                  data-testid="unavail-date-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Turno</label>
                <select
                  value={unavailFormData.shift_type}
                  onChange={(e) => setUnavailFormData({...unavailFormData, shift_type: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                  data-testid="unavail-shift-select"
                >
                  <option value="">Tutto il giorno</option>
                  <option value="mattina">Solo Mattina (9:30-12:30)</option>
                  <option value="pomeriggio">Solo Pomeriggio (15:30-18:30)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo (opzionale)</label>
                <input
                  type="text"
                  value={unavailFormData.reason}
                  onChange={(e) => setUnavailFormData({...unavailFormData, reason: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                  placeholder="Es: Impegni personali, altro lavoro..."
                />
              </div>
              
              <p className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                ⚠️ Come professionista P.IVA, puoi segnare liberamente le tue indisponibilità. 
                Se hai già turni assegnati in questa data, l'admin verrà notificato del conflitto.
              </p>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowUnavailModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving || !unavailFormData.date}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  data-testid="save-unavailability-button"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                  Registra
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Gestione Trimestrale */}
      {showQuarterlyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b flex justify-between items-center bg-gradient-to-r from-indigo-600 to-purple-600">
              <div className="text-white">
                <h3 className="text-lg font-semibold">Gestione Turni Trimestrale</h3>
                <p className="text-indigo-100 text-sm">
                  {isAdmin ? 'Genera turni automatici o visualizza indisponibilità' : 'Seleziona le date in cui non sei disponibile'}
                </p>
              </div>
              <button onClick={handleCloseQuarterlyModal} className="text-white/80 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Selettore Trimestre */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trimestre</label>
                  <select
                    value={selectedQuarter}
                    onChange={(e) => { setSelectedQuarter(parseInt(e.target.value)); setQuarterlyDates([]); }}
                    className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value={1}>Q1 (Gen-Mar)</option>
                    <option value={2}>Q2 (Apr-Giu)</option>
                    <option value={3}>Q3 (Lug-Set)</option>
                    <option value={4}>Q4 (Ott-Dic)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Anno</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => { setSelectedYear(parseInt(e.target.value)); setQuarterlyDates([]); }}
                    className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    {[2025, 2026, 2027].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                
                {isAdmin && (
                  <div className="flex gap-2 ml-auto items-end">
                    <button
                      onClick={handleGenerateQuarterlyShifts}
                      disabled={generatingShifts}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                      data-testid="generate-quarterly-shifts-btn"
                    >
                      {generatingShifts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Genera Turni Q{selectedQuarter}
                    </button>
                    <button
                      onClick={handleDeleteQuarterlyShifts}
                      className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                      data-testid="delete-quarterly-shifts-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                      Elimina Auto-generati
                    </button>
                  </div>
                )}
              </div>
              
              {/* Info Box */}
              {!isAdmin && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
                  <p className="text-indigo-800 text-sm">
                    <strong>📅 Seleziona le date</strong> in cui non sarai disponibile per il trimestre Q{selectedQuarter}/{selectedYear}. 
                    Clicca sulle date per selezionarle/deselezionarle. 
                    <strong className="text-indigo-900"> {quarterlyDates.length} date selezionate</strong>
                  </p>
                </div>
              )}
              
              {/* Calendario Date Trimestre */}
              {!isAdmin && (
                <>
                  <div className="grid grid-cols-7 md:grid-cols-10 lg:grid-cols-14 gap-1 mb-6">
                    {getQuarterDates().map((d, idx) => {
                      const isSelected = quarterlyDates.includes(d.dateStr);
                      const isNewMonth = idx === 0 || getQuarterDates()[idx-1]?.month !== d.month;
                      
                      return (
                        <React.Fragment key={d.dateStr}>
                          {isNewMonth && idx > 0 && <div className="col-span-full h-2" />}
                          {isNewMonth && (
                            <div className="col-span-full text-sm font-semibold text-gray-600 mt-2 mb-1">
                              {d.monthName} {selectedYear}
                            </div>
                          )}
                          <button
                            onClick={() => toggleQuarterlyDate(d.dateStr)}
                            className={`p-2 rounded-lg text-center transition-all ${
                              isSelected 
                                ? 'bg-red-500 text-white shadow-lg scale-105' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            data-testid={`quarterly-date-${d.dateStr}`}
                          >
                            <div className="text-xs opacity-70">{d.dayName}</div>
                            <div className="font-semibold">{d.day}</div>
                          </button>
                        </React.Fragment>
                      );
                    })}
                  </div>
                  
                  {/* Azioni Staff */}
                  <div className="flex flex-wrap gap-3 pt-4 border-t">
                    <button
                      onClick={() => handleSaveQuarterlyUnavailability(null, 'Indisponibilità programmata')}
                      disabled={saving || quarterlyDates.length === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                      data-testid="save-quarterly-all-day"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                      Indisponibile Tutto il Giorno ({quarterlyDates.length})
                    </button>
                    <button
                      onClick={() => handleSaveQuarterlyUnavailability('mattina', 'Indisponibilità mattina')}
                      disabled={saving || quarterlyDates.length === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
                    >
                      Solo Mattina ({quarterlyDates.length})
                    </button>
                    <button
                      onClick={() => handleSaveQuarterlyUnavailability('pomeriggio', 'Indisponibilità pomeriggio')}
                      disabled={saving || quarterlyDates.length === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                    >
                      Solo Pomeriggio ({quarterlyDates.length})
                    </button>
                  </div>
                </>
              )}
              
              {/* Admin: Riepilogo Indisponibilità Staff */}
              {isAdmin && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-700">Riepilogo Indisponibilità Staff Q{selectedQuarter}/{selectedYear}</h4>
                  <p className="text-sm text-gray-500">
                    Genera turni automatici considerando le indisponibilità segnate dallo staff. 
                    I turni verranno creati in stato &quot;pending&quot; e lo staff dovrà confermarli.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-600 text-center">
                      Clicca su <strong>&quot;Genera Turni Q{selectedQuarter}&quot;</strong> per creare automaticamente i turni 
                      per tutti i giorni lavorativi del trimestre, assegnando medici e ortottisti in base alle disponibilità.
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
              <button
                onClick={handleCloseQuarterlyModal}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Template Settimanali - Completo */}
      {showTemplateModal && isAdmin && (
        <TemplateModal 
          templates={templates}
          staff={staff}
          onClose={() => setShowTemplateModal(false)}
          onApply={handleApplyTemplate}
          onRefresh={loadTemplates}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {/* Modal Dettagli Conflitto */}
      {showConflictModal && conflictShiftData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Conflitto Turno</h2>
                </div>
                <button
                  onClick={() => {
                    setShowConflictModal(false);
                    setConflictShiftData(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Info Turno */}
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Turno Assegnato
                </h3>
                <div className="space-y-1 text-sm text-amber-700">
                  <p><strong>Data:</strong> {new Date(conflictShiftData.date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  <p><strong>Orario:</strong> {conflictShiftData.shift_type === 'mattina' ? 'Mattina (9:30-12:30)' : 'Pomeriggio (15:30-18:30)'}</p>
                  <p><strong>Stanza:</strong> {conflictShiftData.room}</p>
                  <p><strong>Stato:</strong> {SHIFT_STATUS[conflictShiftData.status]?.label || conflictShiftData.status}</p>
                </div>
              </div>

              {/* Info Staff */}
              {conflictShiftData.staff_details && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Operatore
                  </h3>
                  <div className="space-y-1 text-sm text-blue-700">
                    <p><strong>Nome:</strong> {conflictShiftData.staff_details.name}</p>
                    <p><strong>Ruolo:</strong> {conflictShiftData.staff_details.staff_type === 'doctor' ? 'Oculista' : 'Ortottista'}</p>
                    {conflictShiftData.staff_details.email && (
                      <p><strong>Email:</strong> {conflictShiftData.staff_details.email}</p>
                    )}
                    {conflictShiftData.staff_details.phone && (
                      <p><strong>Telefono:</strong> {conflictShiftData.staff_details.phone}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Info Indisponibilità */}
              {conflictShiftData.conflict_unavailability && (
                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                  <h3 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                    <Ban className="w-4 h-4" />
                    Indisponibilità Segnalata
                  </h3>
                  <div className="space-y-1 text-sm text-red-700">
                    <p><strong>Data:</strong> {new Date(conflictShiftData.conflict_unavailability.date).toLocaleDateString('it-IT')}</p>
                    {conflictShiftData.conflict_unavailability.shift_type && (
                      <p><strong>Fascia:</strong> {conflictShiftData.conflict_unavailability.shift_type}</p>
                    )}
                    {conflictShiftData.conflict_unavailability.reason && (
                      <p><strong>Motivo:</strong> {conflictShiftData.conflict_unavailability.reason}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Azioni */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={async () => {
                    try {
                      await axios.delete(`${API}/hr/shifts/${conflictShiftData.id}`, getAuthHeaders());
                      toast.success('Turno eliminato');
                      setShowConflictModal(false);
                      setConflictShiftData(null);
                      loadCalendar();
                      loadPendingShifts();
                    } catch (e) {
                      toast.error('Errore eliminazione turno');
                    }
                  }}
                  className="flex-1 py-2.5 px-4 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Elimina Turno
                </button>
                <button
                  onClick={() => {
                    setShowConflictModal(false);
                    setConflictShiftData(null);
                  }}
                  className="flex-1 py-2.5 px-4 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftsManager;
